import { For, Show, createMemo, createSignal, onCleanup, type JSX } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Bar } from '~/components/primitives/bar';
import { Btn } from '~/components/primitives/btn';
import { Field } from '~/components/primitives/field';
import { Readout } from '~/components/primitives/readout';
import { Window } from '~/components/primitives/window';
import { Glyph } from '~/components/glyph';
import { deck } from '~/lib/deck';
import { econLog } from '~/lib/econ-log';
import { economy } from '~/lib/economy';
import { heat } from '~/lib/heat';
import { heatLog } from '~/lib/heat-log';
import { player } from '~/lib/player';
import {
  ARCHETYPE_CUE,
  MATCH_MULT,
  type HackBand,
  type HackResolution,
  resolveHack,
} from '~/lib/resolution';
import { COUNTER_VERB } from '~/lib/tools';
import type { FeedLine, FeedTone, HackPhase, IceType, Target } from '~/types/game';

interface HackModalProps {
  target?: Target;
  onClose: () => void;
}

/** Fallback profile when rendered without a target (testing). In the app a real
 *  node `Target` is always passed. */
const FALLBACK_TARGET: Target = {
  id: 'fallback',
  role: 'target',
  lat: 0,
  lng: 0,
  name: 'UNKNOWN NODE',
  type: 'NODE',
  addr: '192.168.0.0',
  tier: 'T1',
  firewall: '—',
  iceRating: 'LOW',
  iceType: 'WALL',
  risk: 'low',
  rewardValue: 0,
  archetype: 'retail',
  crypto: '—',
  reward: '—',
  history: 'no prior contact',
};

/** Total exec animation window before the resolve phase shows. */
const EXEC_DURATION_MS = 3600;

const BAND_LABEL: Record<HackBand, string> = {
  clean: 'CLEAN BREAK',
  partial: 'PARTIAL PAYLOAD',
  fail: 'SESSION BURNED',
  badfail: 'TRACE BURN',
};
const BAND_TONE: Record<HackBand, 'action' | 'warn' | 'hostile'> = {
  clean: 'action',
  partial: 'warn',
  fail: 'hostile',
  badfail: 'hostile',
};

function AmbientFeed(props: { addr: string }) {
  const lines = createMemo<Array<{ t: string; tag: string; msg: string }>>(() => [
    { t: '−00:08', tag: 'NET', msg: `pinging ${props.addr} · last_seen 0.2s` },
    { t: '−00:06', tag: 'AMB', msg: 'crowd_density 0.41 · nominal' },
    { t: '−00:04', tag: 'NET', msg: 'beacon_hold · awaiting commit' },
    { t: '−00:02', tag: 'AMB', msg: 'rain_intensity 0.32 ↑' },
  ]);
  return (
    <For each={lines()}>
      {(l) => (
        <div style={{ display: 'grid', 'grid-template-columns': '46px 34px 1fr', 'column-gap': '8px' }}>
          <span style={{ color: 'var(--fg-dim)' }}>{l.t}</span>
          <span style={{ color: 'var(--fg-dim)', 'letter-spacing': '0.06em' }}>{l.tag}</span>
          <span style={{ color: 'var(--fg-mute)' }}>{l.msg}</span>
        </div>
      )}
    </For>
  );
}

export function HackModal(props: HackModalProps) {
  const target = (): Target => props.target ?? FALLBACK_TARGET;

  // ── Loadout (read-only here; configured on the DECK screen) ──
  // Decision: the loadout is built on DECK, not in the modal. Prep shows the
  // committed loadout and gates COMMIT on it being non-empty and valid; this
  // keeps the modal focused on the commit decision. (Adjustable-in-prep is a
  // possible future refinement.)
  const loadout = deck.equippedTools;
  const loadoutStrength = () => loadout().reduce((s, t) => s + t.strength, 0);
  const canCommit = () => loadout().length > 0 && deck.isValid();

  // ── Intel: known type (learned) beats the archetype cue ──
  const knownType = (): IceType | null => player.knownType(target().id);
  const cueType = (): IceType => ARCHETYPE_CUE[target().archetype];

  const [phase, setPhase] = createSignal<HackPhase>('prep');
  const [execTime, setExecTime] = createSignal(0);
  const [feed, setFeed] = createSignal<FeedLine[]>([]);
  const [result, setResult] = createSignal<HackResolution | null>(null);
  const [heatGained, setHeatGained] = createSignal(0);

  let feedRef: HTMLDivElement | undefined;
  let timerId: ReturnType<typeof setInterval> | null = null;
  onCleanup(() => {
    if (timerId !== null) clearInterval(timerId);
  });

  const buildFeed = (res: HackResolution): FeedLine[] => {
    const lines: FeedLine[] = [
      { at: 500, tag: 'NET', tone: 'fg', msg: `handshake · ${target().addr}` },
      { at: 1200, tag: 'ICE', tone: 'warn', msg: `defense_type revealed · ${res.revealedType}` },
    ];
    let t = 1900;
    for (const tool of loadout()) {
      const matched = tool.counters === res.revealedType;
      lines.push({
        at: t,
        tag: 'EXP',
        tone: matched ? 'action' : 'fg-dim',
        msg: matched
          ? `${tool.name} → ${COUNTER_VERB[tool.counters]} match ×${MATCH_MULT}`
          : `${tool.name} → off-type · weak`,
      });
      t += 520;
    }
    lines.push({
      at: Math.min(t + 300, EXEC_DURATION_MS - 200),
      tag: 'RES',
      tone: BAND_TONE[res.band] as FeedTone,
      msg:
        res.band === 'clean'
          ? `payload secured · ¤${res.payout.toLocaleString()}`
          : res.band === 'partial'
            ? `partial exfil · ¤${res.payout.toLocaleString()}`
            : res.band === 'badfail'
              ? `counter-trace · ¤${res.penalty} burned`
              : 'session terminated · no payload',
    });
    return lines;
  };

  const commit = () => {
    if (!canCommit()) return;
    const tgt = target();
    // Per-attempt variance lives in these rolls (the resolution fn is pure).
    // Current heat hardens the target's defense via the resolution HEAT HOOK.
    const res = resolveHack(
      tgt,
      loadout(),
      deck.stats(),
      { variance: Math.random(), partial: Math.random() },
      heat.value(),
    );
    setResult(res);

    // Animate the exec window, then resolve. The ¤/intel effects are applied
    // only when the hack COMPLETES (the resolve transition below), not at
    // commit — so navigating away mid-exec (e.g. via the dock) cancels the
    // attempt with no ¤ effect. Completion runs exactly once (the timer is
    // cleared at the boundary), so there is no double-apply.
    const program = buildFeed(res);
    let nextIdx = 0;
    setFeed([]);
    setExecTime(0);
    setPhase('exec');
    const t0 = performance.now();
    timerId = setInterval(() => {
      const dt = performance.now() - t0;
      setExecTime(dt / 1000);
      while (nextIdx < program.length) {
        const line = program[nextIdx];
        if (!line || line.at > dt) break;
        setFeed((f) => [...f, line]);
        nextIdx++;
      }
      if (feedRef) feedRef.scrollTop = feedRef.scrollHeight;
      if (dt >= EXEC_DURATION_MS) {
        if (timerId !== null) clearInterval(timerId);
        timerId = null;
        player.learnType(tgt.id, res.revealedType);
        const netCredits = res.payout - res.penalty;
        player.applyCredits(netCredits);
        // Heat gained on completion (band-keyed, origin = the target), same
        // timing as ¤: an interrupted hack applies neither. The DIAG heat log
        // records every resolution (gain 0 for clean) for tuning.
        const origin = { lat: tgt.lat, lng: tgt.lng };
        const tierNum = Number(tgt.tier.slice(1));
        const gained = heat.gain(res.band, origin, tierNum);
        setHeatGained(gained);
        heatLog.logGain(res.band, gained, origin, tierNum);
        // The DIAG economy log records the ¤ delta (net of any penalty) for
        // payout-per-tier tuning; read-only, after the balance is applied.
        const isDual = tgt.role === 'dual';
        econLog.logEarn({ band: res.band, amount: netCredits, tier: tierNum, nodeId: tgt.id, isDual });
        // Dual node: the hack also affects its vendor (success → pricier,
        // failure → temporary lockout). Pure targets have no vendor. Log the
        // dual consequence AFTER onDualHack so the logged node state is current.
        if (isDual) {
          economy.onDualHack(tgt.id, res.band);
          econLog.logDual(tgt.id, res.band);
        }
        setPhase('resolve');
      }
    }, 80);
  };

  const reattempt = () => {
    if (timerId !== null) clearInterval(timerId);
    timerId = null;
    setPhase('prep');
    setFeed([]);
    setExecTime(0);
    setResult(null);
    setHeatGained(0);
  };

  const headerDot = (): 'action' | 'data' | 'hostile' | '' => {
    if (phase() === 'exec') return 'hostile';
    if (phase() === 'resolve') {
      const b = result()?.band;
      if (b === 'clean') return 'action';
      if (b === 'partial') return 'data';
      return 'hostile';
    }
    return 'data';
  };
  const headerTitle = (): string =>
    phase() === 'exec' ? 'INTRUSION · LIVE' : phase() === 'resolve' ? 'INTRUSION · RESOLVE' : 'INTRUSION · PREP';
  const headerTrailing = (): JSX.Element =>
    phase() === 'exec' ? <span class="blink" style={{ color: 'var(--hostile)' }}>● REC</span> : null;

  const containerStyle: JSX.CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: 'var(--sf-overlay)',
    'backdrop-filter': 'blur(4px)',
    display: 'flex',
    'align-items': 'flex-start',
    'justify-content': 'center',
    'padding-top': '28px',
    'padding-bottom': '14px',
    'z-index': 50,
  };
  const windowStyle: JSX.CSSProperties = {
    width: 'calc(100% - 14px)',
    'max-width': '372px',
    color: 'var(--fg)',
    'max-height': 'calc(100% - 14px)',
  };

  return (
    <div style={containerStyle}>
      <Window
        title={headerTitle()}
        dot={headerDot()}
        headerTrailing={headerTrailing()}
        onClose={phase() !== 'exec' ? props.onClose : undefined}
        style={windowStyle}
      >
        <div style={{ 'overflow-y': 'auto', flex: 1 }} class="no-scroll">
          {/* REGION 1 · TARGET INTEL */}
          <div style={{ padding: '10px 12px', 'border-bottom': '1px solid var(--line-faint)' }}>
            <div style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'margin-bottom': '6px' }}>
              <span style={{ width: '10px', height: '10px', background: 'var(--hostile)', 'flex-shrink': 0 }} />
              <span
                style={{ 'font-family': 'var(--mono)', 'font-size': '13px', color: 'var(--fg)', 'letter-spacing': '0.04em' }}
              >
                {target().name}
              </span>
              <span style={{ flex: 1 }} />
              <Badge tone="hostile">
                {target().tier} · {target().iceRating} ICE
              </Badge>
            </div>
            <div class="t-micro" style={{ 'margin-bottom': '8px' }}>
              {target().type} · {target().addr}
            </div>

            {/* ICE-type intel: learned (known) beats the probabilistic cue. */}
            <div
              class="panel"
              style={{
                padding: '6px 8px',
                'margin-bottom': '8px',
                display: 'flex',
                'align-items': 'center',
                gap: '6px',
                'border-color': knownType() ? 'var(--data-line)' : undefined,
              }}
            >
              <Glyph name={knownType() ? 'unlock' : 'lock'} size={12} color={knownType() ? 'var(--data)' : 'var(--fg-mute)'} />
              <Show
                when={knownType()}
                fallback={
                  <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>
                    ICE PROFILE · likely <span style={{ color: 'var(--warn)' }}>{cueType()}</span> · type
                    reveals on exec
                  </span>
                }
              >
                <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>
                  ICE TYPE · <span style={{ color: 'var(--data)' }}>{knownType()}</span> · known intel
                </span>
              </Show>
            </div>

            <div
              style={{ display: 'grid', 'grid-template-columns': '1fr 1fr', gap: '4px', 'font-family': 'var(--mono)', 'font-size': '10px' }}
            >
              <Field l="REWARD" v={`¤${target().rewardValue.toLocaleString()}`} tone="action" />
              <Field l="RISK" v={target().risk.toUpperCase()} tone={target().risk === 'high' ? 'hostile' : target().risk === 'medium' ? 'warn' : 'fg-mute'} />
              <Field l="FIREWALL" v={target().firewall} tone="hostile" />
              <Field l="CRYPTO" v={target().crypto} tone="data" />
            </div>
          </div>

          {/* REGION 2 · LOADOUT (read-only; built on DECK) */}
          <div style={{ padding: '10px 12px', 'border-bottom': '1px solid var(--line-faint)' }}>
            <div style={{ display: 'flex', 'align-items': 'baseline', 'margin-bottom': '8px' }}>
              <div class="t-label">LOADOUT · {loadout().length} EQUIPPED</div>
              <span style={{ flex: 1 }} />
              <div class="t-micro">
                STR <span style={{ color: 'var(--action)' }}>{loadoutStrength()}</span>
              </div>
            </div>
            <Show
              when={loadout().length > 0}
              fallback={
                <div class="t-micro" style={{ color: 'var(--warn)' }}>
                  No loadout configured — equip tools on the DECK tab to engage.
                </div>
              }
            >
              <div style={{ display: 'flex', 'flex-direction': 'column', gap: '4px' }}>
                <For each={loadout()}>
                  {(t) => {
                    // Once the type is known/revealed, mark matched tools.
                    const revealed = () => result()?.revealedType ?? knownType();
                    const matched = () => revealed() !== null && t.counters === revealed();
                    return (
                      <div
                        style={{
                          display: 'flex',
                          'align-items': 'center',
                          gap: '8px',
                          padding: '6px 8px',
                          border: '1px solid ' + (matched() ? 'var(--action-line)' : 'var(--line)'),
                          background: matched() ? 'var(--action-bg)' : 'var(--sf-input)',
                          'border-radius': 'var(--r-1)',
                        }}
                      >
                        <span class="t-mono" style={{ 'font-size': '11px', color: 'var(--fg)' }}>
                          {t.name}
                        </span>
                        <span style={{ flex: 1 }} />
                        <Show when={matched()}>
                          <Badge tone="action">MATCH</Badge>
                        </Show>
                        <Badge tone="data">{COUNTER_VERB[t.counters]}</Badge>
                        <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>STR {t.strength}</span>
                      </div>
                    );
                  }}
                </For>
              </div>
            </Show>
          </div>

          {/* REGION 3 · TELEMETRY FEED */}
          <div style={{ padding: '10px 12px', position: 'relative' }}>
            <div style={{ display: 'flex', 'align-items': 'center', gap: '6px', 'margin-bottom': '6px' }}>
              <span class="t-label">TELEMETRY</span>
              <span style={{ flex: 1 }} />
              <Show when={phase() === 'prep'}>
                <span class="t-micro" style={{ color: 'var(--fg-dim)' }}>● PRE-ENGAGEMENT</span>
              </Show>
              <Show when={phase() === 'exec'}>
                <span class="t-micro" style={{ color: 'var(--hostile)' }}>● LIVE</span>
              </Show>
              <Show when={phase() === 'resolve' && result()}>
                <span class="t-micro" style={{ color: `var(--${BAND_TONE[result()!.band]})` }}>
                  ● {result()!.band.toUpperCase()}
                </span>
              </Show>
            </div>
            <div
              ref={feedRef}
              class="no-scroll"
              style={{
                height: phase() === 'prep' ? '76px' : '120px',
                background: 'var(--sf-input)',
                border: '1px solid var(--line)',
                padding: '6px 8px',
                overflow: 'auto',
                position: 'relative',
                'font-family': 'var(--mono)',
                'font-size': '10.5px',
                'line-height': '1.5',
                transition: 'height var(--dur-slow) var(--ease)',
              }}
            >
              <Show when={phase() !== 'prep'} fallback={<AmbientFeed addr={target().addr} />}>
                <For each={feed()}>
                  {(ln) => (
                    <div style={{ display: 'grid', 'grid-template-columns': '46px 34px 1fr', 'column-gap': '8px' }}>
                      <span style={{ color: 'var(--fg-dim)' }}>T+{(ln.at / 1000).toFixed(2)}</span>
                      <span style={{ color: `var(--${ln.tone})`, 'letter-spacing': '0.06em' }}>{ln.tag}</span>
                      <span style={{ color: ln.tone === 'fg-dim' ? 'var(--fg-mute)' : 'var(--fg)' }}>{ln.msg}</span>
                    </div>
                  )}
                </For>
              </Show>
              <Show when={phase() === 'exec'}>
                <div class="scanline" />
              </Show>
            </div>
          </div>

          {/* REGION 4 · ACTION SURFACE */}
          <Show when={phase() === 'prep'}>
            <div style={{ padding: '0 12px 12px', display: 'flex', 'flex-direction': 'column', gap: '8px' }}>
              <div class="panel-elev" style={{ padding: '8px', display: 'flex', 'align-items': 'center', gap: '10px' }}>
                <Readout v={`¤${player.credits().toLocaleString()}`} l="BALANCE" tone="action" />
                <Readout v={String(loadoutStrength())} l="LOADOUT STR" tone="data" />
                <Readout v={`¤${target().rewardValue.toLocaleString()}`} l="ON CLEAN" tone="warn" />
                <Readout
                  v={`${Math.round(heat.value())}`}
                  l="HEAT"
                  tone={heat.value() > 50 ? 'hostile' : heat.value() > 25 ? 'warn' : 'data'}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Btn onClick={props.onClose} style={{ flex: 1, 'justify-content': 'center' }}>
                  ABORT
                </Btn>
                <Btn
                  variant="primary"
                  onClick={commit}
                  disabled={!canCommit()}
                  style={{
                    flex: 2,
                    'justify-content': 'center',
                    opacity: canCommit() ? 1 : 0.4,
                    cursor: canCommit() ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Glyph name="arrow" size={12} /> COMMIT
                </Btn>
              </div>
            </div>
          </Show>

          <Show when={phase() === 'exec'}>
            <div style={{ padding: '0 12px 12px' }}>
              <Bar pct={Math.min(100, (execTime() / (EXEC_DURATION_MS / 1000)) * 100)} />
            </div>
          </Show>

          <Show when={phase() === 'resolve' && result()}>
            <div style={{ padding: '0 12px 12px', display: 'flex', 'flex-direction': 'column', gap: '8px' }}>
              <div class="panel-elev" style={{ padding: '10px', 'border-color': `var(--${BAND_TONE[result()!.band]})` }}>
                <div style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'margin-bottom': '6px' }}>
                  <span style={{ width: '10px', height: '10px', background: `var(--${BAND_TONE[result()!.band]})` }} />
                  <span class="t-mono" style={{ 'font-size': '13px', color: 'var(--fg)' }}>
                    {BAND_LABEL[result()!.band]}
                  </span>
                  <span style={{ flex: 1 }} />
                  <Badge tone="data">
                    {result()!.matchedCount}/{result()!.toolCount} MATCH · {result()!.revealedType}
                  </Badge>
                </div>
                <div style={{ display: 'grid', 'grid-template-columns': '1fr 1fr 1fr', gap: '8px' }}>
                  <Show
                    when={result()!.penalty > 0}
                    fallback={
                      <Readout
                        v={`¤${result()!.payout.toLocaleString()}`}
                        l="PAYLOAD"
                        tone={result()!.payout ? 'action' : 'fg-dim'}
                      />
                    }
                  >
                    <Readout v={`−¤${result()!.penalty.toLocaleString()}`} l="PENALTY" tone="hostile" />
                  </Show>
                  <Readout
                    v={heatGained() > 0 ? `+${Math.round(heatGained())}` : '0'}
                    l="HEAT"
                    tone={heatGained() >= 18 ? 'hostile' : heatGained() > 0 ? 'warn' : 'fg-dim'}
                  />
                  <Readout v={`¤${player.credits().toLocaleString()}`} l="BALANCE" tone="data" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Btn onClick={reattempt} style={{ flex: 1, 'justify-content': 'center' }}>
                  RE-RUN
                </Btn>
                <Btn variant="primary" onClick={props.onClose} style={{ flex: 1, 'justify-content': 'center' }}>
                  EXIT
                </Btn>
              </div>
            </div>
          </Show>
        </div>
      </Window>
    </div>
  );
}
